import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Users, 
  BarChart3, 
  Headphones, 
  Shield, 
  Zap,
  ArrowRight,
  Check,
  PhoneCall,
  Activity,
  MessageSquare,
  FileText,
  UserCheck,
  Settings,
  Globe,
  Clock,
  Mic,
  Volume2,
  PhoneForwarded,
  PhoneIncoming,
  History,
  PieChart,
  Building2,
  Lock,
  Smartphone,
  Monitor,
  Layers,
  Handshake,
  Plug
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Features() {
  // Set page title
  useEffect(() => {
    document.title = "Connect | Features";
    window.scrollTo(0, 0);
  }, []);

  const mainFeatures = [
    {
      icon: Phone,
      title: 'WebRTC Softphone',
      description: 'Make and receive calls directly from your browser. No downloads, no plugins — just open and call.',
      highlights: ['HD voice quality', 'Works on any device', 'Encrypted calls']
    },
    {
      icon: Users,
      title: 'Real-time Presence',
      description: 'See who\'s available, on a call, or away. Know your team\'s status at a glance.',
      highlights: ['Live status updates', 'Busy indicators', 'Extension visibility']
    },
    {
      icon: Headphones,
      title: 'Queue Monitoring',
      description: 'Live dashboard showing active calls, waiting callers, and queue performance metrics.',
      highlights: ['Live call tracking', 'Wait time alerts', 'Agent availability']
    },
    {
      customImage: '/features/analytics-dashboard.png',
      title: 'Analytics Dashboard',
      description: 'Comprehensive reporting on call volumes, durations, answer rates, and team performance.',
      highlights: ['Custom date ranges', 'Export reports', 'Trend analysis']
    },
    {
      customImage: '/features/call-transfer.png',
      title: 'Call Transfer',
      description: 'Seamlessly transfer calls with blind or attended transfer options. Never lose a customer.',
      highlights: ['Blind transfer', 'Attended transfer', 'Warm handoffs']
    },
    {
      customImage: '/features/ai-transcripts.png',
      title: 'AI Transcripts',
      description: 'Automatic call transcription with AI-powered summaries, sentiment analysis, and action items.',
      highlights: ['Auto transcription', 'Sentiment scoring', 'Action items']
    }
  ];

  const additionalFeatures = [
    { icon: History, title: 'Call History', desc: 'Complete CDR with search and filters' },
    { icon: MessageSquare, title: 'Voicemail', desc: 'Visual voicemail with audio playback' },
    { icon: UserCheck, title: 'Contact Management', desc: 'Sync contacts from your PBX' },
    { icon: Lock, title: 'Role-based Access', desc: 'Control who sees what' },
    { icon: Building2, title: 'Global Contacts', desc: 'Shared company-wide directory' },
    { icon: Settings, title: 'Device Settings', desc: 'Choose mic and speaker devices' },
    { icon: Mic, title: 'Mute Controls', desc: 'Quick mute/unmute during calls' },
    { icon: Volume2, title: 'Hold & Resume', desc: 'Put calls on hold with music' },
    { icon: Globe, title: 'Click-to-Call', desc: 'Call any number with one click' },
    { icon: Clock, title: 'Call Timer', desc: 'Track call duration in real-time' },
    { icon: PieChart, title: 'Usage Reports', desc: 'Monitor team call activity' },
    { icon: Smartphone, title: 'Mobile Ready', desc: 'Works on tablets and phones' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold text-white tracking-tight">CONNECT</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-cyan-400 font-medium">Features</Link>
            <Link to="/pricing" className="text-slate-300 hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="text-slate-300 hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="text-slate-300 hover:text-white transition-colors">Partners</Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                Sign In
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/25">
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-8">
            <Building2 className="w-4 h-4" />
            Built for New Zealand Business
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Features Your Team
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Will Love
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            A complete cloud PBX operator console with softphone, presence, analytics, and AI-powered insights — ready to use in minutes.
          </p>
        </div>
      </section>

      {/* Screenshot Section 1 - Softphone */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">WebRTC Softphone</h2>
            <p className="text-slate-400">Make and receive calls directly from your browser</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl shadow-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/5 to-blue-500/5"></div>
            <div className="bg-slate-900 p-2">
              <div className="flex items-center gap-2 mb-2 px-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="ml-4 text-xs text-slate-500">CONNECT - My Phone</span>
              </div>
              <div className="rounded-lg overflow-hidden bg-slate-950">
                <div className="aspect-video bg-gradient-to-br from-slate-900 to-slate-950 flex items-center justify-center relative">
                  <div className="absolute inset-0 flex">
                    {/* Sidebar mockup */}
                    <div className="w-48 bg-slate-900/80 border-r border-slate-800 p-4 space-y-3">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                          <PhoneCall className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-white text-sm">CONNECT</span>
                      </div>
                      {['My Phone', 'Contacts', 'My Calls', 'Queue Monitor', 'Call History', 'Analytics'].map((item, i) => (
                        <div key={i} className={`px-3 py-2 rounded-lg text-sm ${i === 0 ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400'}`}>
                          {item}
                        </div>
                      ))}
                    </div>
                    {/* Dialer content */}
                    <div className="flex-1 p-6">
                      <div className="flex gap-6">
                        <div className="flex-1 bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
                          <h3 className="text-white font-medium mb-4">Softphone</h3>
                          <div className="text-center mb-4">
                            <div className="text-2xl text-slate-300 mb-2">Enter number</div>
                            <div className="text-sm text-green-400">● Registered</div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 max-w-[180px] mx-auto">
                            {[1,2,3,4,5,6,7,8,9,'*',0,'#'].map((n, i) => (
                              <div key={i} className="w-12 h-12 rounded-lg bg-slate-700/50 flex items-center justify-center text-white font-medium">
                                {n}
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 flex justify-center">
                            <div className="px-8 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium">
                              Call
                            </div>
                          </div>
                        </div>
                        {/* Contacts panel */}
                        <div className="w-64 bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
                          <h3 className="text-white font-medium mb-3">Contacts</h3>
                          {['John Smith', 'Sarah Connor', 'Mike Johnson', 'Emma Wilson'].map((name, i) => (
                            <div key={i} className="flex items-center gap-3 py-2 px-2 rounded hover:bg-slate-700/30">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center text-xs text-cyan-400">
                                {name.split(' ').map(n => n[0]).join('')}
                              </div>
                              <div>
                                <div className="text-sm text-white">{name}</div>
                                <div className="text-xs text-slate-500">Ext. {1001 + i}</div>
                              </div>
                              <div className={`w-2 h-2 rounded-full ml-auto ${i % 2 === 0 ? 'bg-green-500' : 'bg-slate-500'}`}></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshot Section 2 - Queue Monitor */}
      <section className="py-12 px-6 bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Queue Monitor</h2>
            <p className="text-slate-400">Real-time view of active calls and team presence</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl shadow-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/5 to-blue-500/5"></div>
            <div className="bg-slate-900 p-2">
              <div className="flex items-center gap-2 mb-2 px-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="ml-4 text-xs text-slate-500">CONNECT - Queue Monitor</span>
              </div>
              <div className="rounded-lg overflow-hidden bg-slate-950">
                <div className="aspect-video bg-gradient-to-br from-slate-900 to-slate-950 flex items-center justify-center relative">
                  <div className="absolute inset-0 flex">
                    {/* Sidebar */}
                    <div className="w-48 bg-slate-900/80 border-r border-slate-800 p-4 space-y-3">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                          <PhoneCall className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-white text-sm">CONNECT</span>
                      </div>
                      {['My Phone', 'Contacts', 'My Calls', 'Queue Monitor', 'Call History', 'Analytics'].map((item, i) => (
                        <div key={i} className={`px-3 py-2 rounded-lg text-sm ${i === 3 ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400'}`}>
                          {item}
                        </div>
                      ))}
                    </div>
                    {/* Queue content */}
                    <div className="flex-1 p-6">
                      <h3 className="text-xl font-semibold text-white mb-4">Active Calls</h3>
                      <div className="grid gap-3">
                        {[
                          { from: '+64 21 555 1234', to: 'Sarah Mitchell', status: 'In Progress', duration: '03:45', color: 'green' },
                          { from: '+64 9 123 4567', to: 'Sales Queue', status: 'Ringing', duration: '00:12', color: 'yellow' },
                          { from: 'James Wilson', to: '+64 27 888 9999', status: 'In Progress', duration: '01:22', color: 'green' },
                        ].map((call, i) => (
                          <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700/30">
                            <div className={`w-3 h-3 rounded-full ${call.color === 'green' ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`}></div>
                            <div className="flex-1">
                              <div className="text-white font-medium">{call.from}</div>
                              <div className="text-sm text-slate-400">→ {call.to}</div>
                            </div>
                            <div className="text-right">
                              <div className={`text-sm ${call.color === 'green' ? 'text-green-400' : 'text-yellow-400'}`}>{call.status}</div>
                              <div className="text-xs text-slate-500">{call.duration}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 grid grid-cols-3 gap-4">
                        <div className="p-4 rounded-lg bg-slate-800/30 text-center">
                          <div className="text-2xl font-bold text-cyan-400">3</div>
                          <div className="text-xs text-slate-500">Active Calls</div>
                        </div>
                        <div className="p-4 rounded-lg bg-slate-800/30 text-center">
                          <div className="text-2xl font-bold text-green-400">5</div>
                          <div className="text-xs text-slate-500">Available</div>
                        </div>
                        <div className="p-4 rounded-lg bg-slate-800/30 text-center">
                          <div className="text-2xl font-bold text-slate-400">2</div>
                          <div className="text-xs text-slate-500">Away</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshot Section 3 - Analytics */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Call Analytics</h2>
            <p className="text-slate-400">Comprehensive dashboards with actionable insights</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl shadow-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/5 to-blue-500/5"></div>
            <div className="bg-slate-900 p-2">
              <div className="flex items-center gap-2 mb-2 px-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="ml-4 text-xs text-slate-500">CONNECT - Analytics</span>
              </div>
              <div className="rounded-lg overflow-hidden bg-slate-950">
                <div className="aspect-video bg-gradient-to-br from-slate-900 to-slate-950 flex items-center justify-center relative">
                  <div className="absolute inset-0 flex">
                    {/* Sidebar */}
                    <div className="w-48 bg-slate-900/80 border-r border-slate-800 p-4 space-y-3">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                          <PhoneCall className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-white text-sm">CONNECT</span>
                      </div>
                      {['My Phone', 'Contacts', 'My Calls', 'Queue Monitor', 'Call History', 'Analytics'].map((item, i) => (
                        <div key={i} className={`px-3 py-2 rounded-lg text-sm ${i === 5 ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400'}`}>
                          {item}
                        </div>
                      ))}
                    </div>
                    {/* Analytics content */}
                    <div className="flex-1 p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-semibold text-white">Analytics Dashboard</h3>
                        <div className="px-3 py-1 rounded bg-slate-800 text-xs text-slate-400">Last 30 days</div>
                      </div>
                      {/* Stats row */}
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        {[
                          { label: 'Total Calls', value: '1,247', change: '+12%', color: 'cyan' },
                          { label: 'Answered', value: '1,089', change: '+8%', color: 'green' },
                          { label: 'Missed', value: '158', change: '-5%', color: 'red' },
                          { label: 'Avg Duration', value: '4:32', change: '+15s', color: 'blue' },
                        ].map((stat, i) => (
                          <div key={i} className="p-4 rounded-lg bg-slate-800/30 border border-slate-700/30">
                            <div className="text-xs text-slate-500 mb-1">{stat.label}</div>
                            <div className="text-2xl font-bold text-white">{stat.value}</div>
                            <div className={`text-xs ${stat.color === 'red' ? 'text-red-400' : 'text-green-400'}`}>{stat.change}</div>
                          </div>
                        ))}
                      </div>
                      {/* Chart mockup */}
                      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                        <div className="text-sm text-white mb-4">Call Volume by Hour</div>
                        <div className="flex items-end gap-1 h-24">
                          {[30, 45, 60, 80, 95, 100, 85, 70, 55, 40, 50, 65, 75, 90, 85, 70, 55, 45, 35, 25, 20, 15, 10, 8].map((h, i) => (
                            <div 
                              key={i} 
                              className="flex-1 bg-gradient-to-t from-cyan-500 to-blue-500 rounded-t opacity-80"
                              style={{ height: `${h}%` }}
                            ></div>
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500 mt-2">
                          <span>12am</span>
                          <span>6am</span>
                          <span>12pm</span>
                          <span>6pm</span>
                          <span>12am</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshot Section 4 - AI Transcripts */}
      <section className="py-12 px-6 bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">AI Transcripts & Analysis</h2>
            <p className="text-slate-400">Automatic transcription with sentiment analysis and summaries</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl shadow-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/5 to-blue-500/5"></div>
            <div className="bg-slate-900 p-2">
              <div className="flex items-center gap-2 mb-2 px-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="ml-4 text-xs text-slate-500">CONNECT - Transcripts</span>
              </div>
              <div className="rounded-lg overflow-hidden bg-slate-950">
                <div className="aspect-video bg-gradient-to-br from-slate-900 to-slate-950 flex items-center justify-center relative">
                  <div className="absolute inset-0 flex">
                    {/* Sidebar */}
                    <div className="w-48 bg-slate-900/80 border-r border-slate-800 p-4 space-y-3">
                      <div className="flex items-center gap-2 mb-6">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                          <PhoneCall className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-white text-sm">CONNECT</span>
                      </div>
                      {['My Phone', 'Contacts', 'My Calls', 'Queue Monitor', 'Call History', 'Analytics'].map((item, i) => (
                        <div key={i} className={`px-3 py-2 rounded-lg text-sm ${i === 2 ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400'}`}>
                          {item}
                        </div>
                      ))}
                    </div>
                    {/* Transcript content */}
                    <div className="flex-1 p-6 overflow-hidden">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs">Transcripts</div>
                      </div>
                      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500/30 to-emerald-500/30 flex items-center justify-center text-green-400">
                            <PhoneIncoming className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-white font-medium">+64 21 345 6789</div>
                            <div className="text-xs text-slate-500">Jan 30, 2025 • 4:32 duration</div>
                          </div>
                          <div className="ml-auto px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs">Positive</div>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-slate-900/50 p-3 rounded text-sm">
                            <div className="text-xs text-slate-500 mb-1">Transcript:</div>
                            <div className="text-slate-300 text-xs leading-relaxed">
                              [00:00:02] Hello, this is Sarah calling about the project update...<br/>
                              [00:00:08] Yes, I can confirm we're on track for delivery...<br/>
                              [00:00:15] The team has completed the main features...
                            </div>
                          </div>
                          <div className="bg-slate-900/50 p-3 rounded text-sm">
                            <div className="text-xs text-slate-500 mb-1">AI Analysis:</div>
                            <div className="text-slate-300 text-xs">
                              <span className="text-cyan-400">Summary:</span> Project status update call. Customer confirmed satisfaction with progress.<br/>
                              <span className="text-cyan-400">Sentiment:</span> Positive (0.8)<br/>
                              <span className="text-cyan-400">Action Items:</span> Send follow-up email with timeline
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Powerful features for your team
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Everything you need to deliver exceptional service and manage your team effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainFeatures.map((feature, i) => (
              <div 
                key={i}
                className="group p-8 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 hover:border-cyan-500/30 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center mb-6 group-hover:from-cyan-500/30 group-hover:to-blue-600/30 transition-all overflow-hidden">
                  {feature.customImage ? (
                    <img 
                      src={feature.customImage} 
                      alt={feature.title} 
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <feature.icon className="w-7 h-7 text-cyan-400" />
                  )}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 mb-4 leading-relaxed">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.highlights.map((highlight, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-cyan-400" />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              And so much more...
            </h2>
            <p className="text-lg text-slate-400">
              Every feature you need, nothing you don't.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {additionalFeatures.map((feature, i) => (
              <div 
                key={i}
                className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 hover:border-cyan-500/20 transition-colors"
              >
                <feature.icon className="w-5 h-5 text-cyan-400 mb-2" />
                <h4 className="text-white font-medium text-sm mb-1">{feature.title}</h4>
                <p className="text-slate-500 text-xs">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-6">
              <Plug className="w-4 h-4" />
              Platform Integrations
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Works with Leading Cloud PBX Providers
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Integrate CONNECT with your existing PBX platform and offer your customers a modern web console.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Cloud PBX Integration Card */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 hover:border-cyan-500/30 transition-all duration-300 group">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-all">
                  <Globe className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Cloud PBX</h3>
                  <p className="text-slate-400 text-sm">NZ Cloud PBX Provider</p>
                </div>
                <span className="ml-auto px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                  Available
                </span>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                Full integration including SIP connectivity, call data records, AI transcription, and real-time presence.
              </p>
              <div className="flex flex-wrap gap-2">
                {['SIP/WebRTC', 'Call History', 'AI Transcripts', 'Presence'].map((tag, i) => (
                  <span key={i} className="px-2 py-1 rounded-full bg-slate-700/50 text-slate-300 text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Coming Soon Card */}
            <div className="p-6 rounded-2xl bg-slate-800/20 border border-slate-700/30 border-dashed">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-slate-700/30 flex items-center justify-center">
                  <Plug className="w-7 h-7 text-slate-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-400">More Coming Soon</h3>
                  <p className="text-slate-500 text-sm">Additional providers</p>
                </div>
                <span className="ml-auto px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
                  Roadmap
                </span>
              </div>
              <p className="text-slate-500 text-sm mb-4">
                We're actively working on integrations with additional Cloud PBX providers.
              </p>
              <p className="text-slate-500 text-sm">
                Have a specific provider in mind? <Link to="/help" className="text-cyan-400 hover:underline">Let us know</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reseller CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-cyan-600/10 to-blue-600/10 border-y border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-6">
            <Handshake className="w-4 h-4" />
            Reseller Program
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Offer CONNECT to Your Customers
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Join our reseller program and get wholesale pricing. NFR subscriptions available for partners to demo the platform.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/partner/login?tab=register">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-6 text-lg shadow-xl shadow-cyan-500/25">
                Become a Reseller
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-6 text-lg">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-bold">CONNECT</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/features" className="hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="hover:text-white transition-colors">Partners</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-slate-500">© 2025 Connect-US. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
